import { inject, Injectable, TemplateRef } from '@angular/core';
import { FormGroup } from '@angular/forms';
import { AuthService, LinksService } from '@isophi/core-legacy';
import { MenuItem } from '@isophi/core-ui';
import { GenderEnum, RelativeTypeEnum } from '@isophi/mng-api';
import { TranslocoService } from '@jsverse/transloco';
import { NgbDateParserFormatter, NgbDateStruct } from '@ng-bootstrap/ng-bootstrap';
import { DAYS_OF_WEEK } from 'angular-calendar';
import { isWithinInterval, parseISO } from 'date-fns';
import { BehaviorSubject, map, Subject, tap } from 'rxjs';

import { environment } from '../../../environments/environment';
import { MenuTypeEnum } from '../enums/menu-type.enum';
import { RouterLinkFactory } from '../router/router-link.factory';
import { KindergartenService } from './kindergarten.service';

const female = [RelativeTypeEnum.Mother, RelativeTypeEnum.StepMother, RelativeTypeEnum.Grandmother, RelativeTypeEnum.Aunt];

export interface HeaderInput {
  icon?: string;
  placeholder?: string;
  formGroup: FormGroup;
  formControlName: string;
}
export interface HeaderButton {
  style: 'outline' | 'primary';
  text?: string;
  icon?: string;
  tooltip?: string;
  keepOnTop?: boolean;
  onClick: () => void;
}

@Injectable({
  providedIn: 'root',
})
export class HelperService {
  private readonly linksService = inject(LinksService);

  titleChanged$: BehaviorSubject<string> = new BehaviorSubject(null);

  smallTextTitle$: BehaviorSubject<string> = new BehaviorSubject(null);

  showSubMenu$: BehaviorSubject<{ show: boolean; menuType: MenuTypeEnum }> = new BehaviorSubject(null);

  showGroupAndChildrenFilter$: BehaviorSubject<{ showGroup: boolean; showChildren: boolean }> = new BehaviorSubject(null);

  data;

  toggleSidebar$: Subject<string> = new Subject();

  showHeader$ = new BehaviorSubject<boolean>(true);

  state$ = new BehaviorSubject<boolean | null>(null);

  readonly inputs$ = new BehaviorSubject<HeaderInput[] | null>(null);

  readonly buttons$ = new BehaviorSubject<HeaderButton[] | null>(null);

  readonly subHeaderTemplate$ = new BehaviorSubject<TemplateRef<unknown> | null>(null);

  /** Force logged-user component to condense */
  readonly condenseProfile$ = new BehaviorSubject(false);

  private readonly transloco = inject(TranslocoService);
  private readonly links = inject(RouterLinkFactory);
  private readonly ngbDateParserFormatter = inject(NgbDateParserFormatter);
  private readonly authService = inject(AuthService);
  private readonly kindergartenService = inject(KindergartenService);

  private get isManager(): boolean {
    return this.authService.loggedUser?.teacher?.isManager() ?? false;
  }

  enrollmentState$ = this.kindergartenService.getEnrollmentState().pipe(
    map((state) => {
      return state.eEnrollmentState;
    }),
    tap((state) => {
      this.state$.next(state);
    })
  );

  get managementMenuItems(): MenuItem[] {
    const items = [
      {
        url: this.links.dashboard().join('/'),
        icon: 'dashboard.png',
        menuText: this.transloco.translate('dashboard/title'),
        title: this.transloco.translate('dashboard/title'),
        exactMatch: false,
      },
      {
        url: this.links.attendanceToday().join('/'),
        icon: 'attendance-today.png',
        menuText: this.transloco.translate('evidence/today'),
        title: this.transloco.translate('evidence/attendanceToday'),
        exactMatch: true,
      },
      {
        url: this.links.attendanceOverview().join('/'),
        icon: 'attendance-overview.png',
        menuText: this.transloco.translate('evidence/overview'),
        title: this.transloco.translate('evidence/attendanceOverview'),
        exactMatch: true,
      },
      {
        url: this.links.gradebookList().join('/'),
        icon: 'gradebook.png',
        menuText: this.transloco.translate('gradebook/title'),
        title: this.transloco.translate('gradebook/title'),
        exactMatch: false,
      },
      {
        url: this.links.announcementList().join('/'),
        icon: 'announcement.png',
        menuText: this.transloco.translate('announcement/title'),
        title: this.transloco.translate('announcement/title'),
        exactMatch: false,
        divider: true,
      },
      {
        url: this.links.children().join('/'),
        icon: 'children.png',
        menuText: this.transloco.translate('sidebar/children'),
        title: this.transloco.translate('child/title'),
        exactMatch: false,
      },
      {
        url: this.links.groups().join('/'),
        icon: 'groups.png',
        menuText: this.transloco.translate('groups/title'),
        title: this.transloco.translate('groups/title'),
        exactMatch: false,
      },
    ];

    if (this.isManager) {
      items.push({
        url: this.links.exportMsmt().join('/'),
        icon: 'export-msmt.png',
        menuText: this.transloco.translate('exportMsmt/title'),
        title: this.transloco.translate('exportMsmt/title'),
        exactMatch: true,
      });
    }

    return items;
  }

  get childrenPortfolioMenuItems(): MenuItem[] {
    const items = [
      {
        url: this.links.childrenPortfolioDashboard().join('/'),
        icon: 'dashboard.png',
        menuText: this.transloco.translate('dashboard/title'),
        title: this.transloco.translate('dashboard/title'),
        exactMatch: false,
        divider: true,
      },
      {
        url: this.links.childrenPortfolio().join('/'),
        subtitle: this.transloco.translate('childrenPortfolio/portfolio/subtitle'),
        icon: 'children-portfolio.png',
        menuText: this.transloco.translate('childrenPortfolio/portfolio'),
        title: this.transloco.translate('childrenPortfolio/portfolio'),
        exactMatch: false,
        divider: true,
      },
      {
        url: this.links.childrenPortfolioClassDiary().join('/'),
        subtitle: this.transloco.translate('childrenPortfolio/classSection/subtitle'),
        icon: 'ship-log-scroll.png',
        colorIcon: true,
        menuText: this.transloco.translate('childrenPortfolio/diary/menu'),
        title: this.transloco.translate('childrenPortfolio/diary/menu'),
        exactMatch: false,
      },
      {
        url: this.links.childrenPortfolioKorabClass().join('/'),
        icon: 'korab-ship.png',
        colorIcon: true,
        menuText: this.transloco.translate('childrenPortfolio/korab'),
        title: this.transloco.translate('childrenPortfolio/korab'),
        exactMatch: false,
      },
      {
        url: this.links.childrenPortfolioShipOfKnowledge().join('/'),
        icon: 'sailing.png',
        menuText: this.transloco.translate('childrenPortfolio/shipOfKnowledge'),
        title: this.transloco.translate('childrenPortfolio/shipOfKnowledge'),
        exactMatch: false,
        // POR-001 §14, image 5b: Lodní deník and the two Koráb items form their own group
        divider: true,
      },
      {
        url: this.links.childrenPortfolioSkill().join('/'),
        icon: 'skills-target.png',
        menuText: this.transloco.translate('childrenPortfolio/skills'),
        title: this.transloco.translate('childrenPortfolio/skills'),
        exactMatch: false,
      },
      {
        url: this.links.childrenPortfolioCompetence().join('/'),
        icon: 'competence-head.png',
        menuText: this.transloco.translate('childrenPortfolio/competences'),
        title: this.transloco.translate('childrenPortfolio/competences'),
        exactMatch: false,
      },
      {
        url: this.links.childrenPortfolioLiteracy().join('/'),
        icon: 'literacy-book.png',
        menuText: this.transloco.translate('childrenPortfolio/literacies'),
        title: this.transloco.translate('childrenPortfolio/literacies'),
        exactMatch: false,
      },
      {
        url: this.links.childrenPortfolioActivity().join('/'),
        icon: 'activity.png',
        menuText: this.transloco.translate('childrenPortfolio/activities'),
        title: this.transloco.translate('childrenPortfolio/activities'),
        exactMatch: false,
        divider: true,
      },
      {
        url: this.links.children().join('/'),
        icon: 'children.png',
        menuText: this.transloco.translate('sidebar/children'),
        title: this.transloco.translate('child/title'),
        exactMatch: false,
      },
      {
        url: this.links.groups().join('/'),
        icon: 'groups.png',
        menuText: this.transloco.translate('groups/title'),
        title: this.transloco.translate('groups/title'),
        exactMatch: false,
      },
    ];

    return items;
  }

  get papMenuItems(): MenuItem[] {
    const papAppUrl = this.linksService.getPapAppUrl();
    const items = [
      {
        url: this.links.papPortfolio().join('/'),
        subtitle: this.transloco.translate('pap/portfolio/subtitle'),
        icon: 'pap-portfolio.png',
        menuText: this.transloco.translate('pap/portfolio'),
        title: this.transloco.translate('pap/portfolio/title'),
        exactMatch: false,
      },
      {
        url: this.links.papPlan().join('/'),
        icon: 'pap-plan.png',
        menuText: this.transloco.translate('pap/plan'),
        title: this.transloco.translate('pap/plan'),
        exactMatch: false,
      },
      {
        url: this.links.papAutoEvaluation().join('/'),
        icon: 'pap-auto-evaluation.png',
        menuText: this.transloco.translate('pap/autoEvaluation'),
        title: this.transloco.translate('pap/autoEvaluation'),
        exactMatch: false,
        divider: true,
      },
      {
        url: papAppUrl.url,
        isHref: true,
        icon: 'pap-my-record.png',
        targetId: papAppUrl.target,
        menuText: this.transloco.translate('pap/myRecord'),
        title: this.transloco.translate('pap/myRecord'),
        exactMatch: false,
      },
    ];

    return items;
  }

  kindergartenAdminMenuItems(hasTestingProduct: boolean, isDirector: boolean, isManager: boolean): MenuItem[] {
    const items: MenuItem[] = [
      {
        url: this.links.kindergartenData().join('/'),
        icon: 'kindergarten-data.png',
        menuText: this.transloco.translate('settings/kindergartenData'),
        title: this.transloco.translate('settings/kindergartenData'),
        exactMatch: false,
        subtitle: this.transloco.translate('settings/title'),
      },
    ];

    if (isDirector || isManager) {
      items.push({
        url: this.links.organizationWorkplaces().join('/'),
        icon: 'workplace-licenses.png',
        menuText: this.transloco.translate('settings/organizationAndLicenses'),
        title: this.transloco.translate('settings/organizationAndLicenses'),
        exactMatch: false,
      });
    }

    items.push({
      url: this.links.priceList().join('/'),
      icon: 'prices.png',
      menuText: this.transloco.translate('settings/priceList'),
      title: this.transloco.translate('settings/priceList'),
      exactMatch: false,
    });

    items.push(
      {
        url: this.links.userList().join('/'),
        icon: 'users.png',
        menuText: this.transloco.translate('user/title'),
        title: this.transloco.translate('user/title'),
        exactMatch: false,
      },
      {
        url: this.links.holidaysDates().join('/'),
        icon: 'holidays-dates.png',
        menuText: this.transloco.translate('settings/daysOff'),
        title: this.transloco.translate('settings/daysOff'),
        exactMatch: false,
        divider: true,
      },
      {
        url: this.links.enrollment().join('/'),
        icon: 'enrollment.png',
        menuText: this.transloco.translate('enrollment/title'),
        title: this.transloco.translate('enrollment/title'),
        exactMatch: false,
        enrollmentState$: this.enrollmentState$,
      }
    );

    return items;
  }

  setTitle(pathnameFromMenuItems?: string, customTitle?: string): void {
    this.showGroupAndChildrenFilter$.next(null);
    if (pathnameFromMenuItems) {
      const menuItem = this.managementMenuItems.find((item) => pathnameFromMenuItems.includes(item.url));
      const subMenuItem = menuItem?.subMenu ? menuItem.subMenu.find((item) => pathnameFromMenuItems.includes(item.url)) : null;
      const subItem = menuItem?.subItems ? menuItem.subItems.find((item) => pathnameFromMenuItems.includes(item.url)) : null;
      const title = subMenuItem ? subMenuItem.title : subItem ? subItem.title : menuItem ? menuItem.title : '';
      this.titleChanged$.next(title);
      return;
    }

    if (customTitle) {
      const title = customTitle ? customTitle : '';
      this.titleChanged$.next(title);
    }
  }

  setSmallTextTitle(smallText: string): void {
    this.smallTextTitle$.next(smallText);
  }

  clearSmallTextTitle(): void {
    this.smallTextTitle$.next(null);
  }

  getTagImageUrl(url: string | null | undefined): string {
    return url ? `${environment.materialServer}${url}` : '';
  }

  showSubMenu(show: boolean, menuType?: MenuTypeEnum, data?): void {
    if (show) {
      this.data = data;
    }
    this.showSubMenu$.next({ show, menuType });
  }

  prepareData(dataObject: { [key: string]: string }, enumObject): { [key: string]: string }[] {
    const data = [];
    for (const val in enumObject) {
      const value = enumObject[val];
      data.push({ text: dataObject[value], value });
    }
    return data;
  }

  formateDate(date): string | null {
    return date ? this.ngbDateParserFormatter.format(date).split('.').reverse().join('-') : null;
  }

  parseDate(date: string | null): NgbDateStruct {
    return date ? this.ngbDateParserFormatter.parse(date.split('-').reverse().join('.')) : null;
  }

  getGender(type: RelativeTypeEnum): GenderEnum {
    // currently if not female, then male, will be fixed in future
    return female.includes(type) ? GenderEnum.Female : GenderEnum.Male;
  }

  checkHolidayDates(date: NgbDateStruct, holidays): boolean {
    const day = new Date(date.year, date.month - 1, date.day).getDay();

    // Check if the date is a weekend
    const isWeekend = day === DAYS_OF_WEEK.SUNDAY || day === DAYS_OF_WEEK.SATURDAY;

    // Convert NgbDateStruct to a Date object
    const currentDate = new Date(date.year, date.month - 1, date.day);

    // Check if the date is a holiday or falls within a holiday range
    const isHoliday = this.isHoliday(currentDate, holidays);

    return isWeekend || isHoliday;
  }

  // Check if a date falls on a holiday
  isHoliday(date: Date, holidays): boolean {
    return holidays.some((holiday) => {
      const fromDate = parseISO(holiday.date);
      const toDate = holiday.dateTo ? parseISO(holiday.dateTo) : fromDate;
      return isWithinInterval(date, { start: fromDate, end: toDate });
    });
  }

  setHeaderInputs(inputs: HeaderInput[]) {
    this.inputs$.next(inputs);
  }
  setHeaderButtons(buttons: HeaderButton[]) {
    this.buttons$.next(buttons);
  }

  clearHeaderInputs() {
    this.inputs$.next(null);
  }
  clearHeaderButtons() {
    this.buttons$.next(null);
  }

  setSubHeaderTemplate(tpl: TemplateRef<unknown>): void {
    this.subHeaderTemplate$.next(tpl);
  }

  clearSubHeaderTemplate(): void {
    this.subHeaderTemplate$.next(null);
  }
}
